import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { isPresent } from '@mtna/core-ts';
import { CanDestroyNotify, mixinDestroyNotifier } from '@mtna/core-angular';
import { Variable as ConsumerVariable } from '@mtna/pojo-consumer-ts';
import { Variable as ManagementVariable } from '@mtna/pojo-management-ts';
import { AmChartsFrequencySetData } from '@rds/rds-base-ts';
import { skipWhile, take } from 'rxjs/operators';

import { createVerticalBarChart } from './chart-util';

import type { XYChart } from '@amcharts/amcharts4/charts';

const RdsVariableStatsBase = mixinDestroyNotifier(class {});

/**
 * Display variable statistics from the `summaryStatistics` and `frequencies`.
 *
 * Note: the heading levels start at `<h3>`, so there is an assumption an h1 and h2 preceeds this component.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-variable-stats',
  templateUrl: 'variable-stats.component.html',
  styleUrls: ['variable-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsVariableStatsComponent<V extends ConsumerVariable | ManagementVariable>
  extends RdsVariableStatsBase
  implements CanDestroyNotify, OnDestroy
{
  static ngAcceptInputType_loadingFrequencyChart: BooleanInput;

  /** The element to render the frequency chart in. */
  @ViewChildren('frequencyChart') frequencyChartElements: QueryList<ElementRef> | undefined;
  /** The element to render the populated and missing chart in. */
  @ViewChildren('populatedAndMissingChart') populatedAndMissingChartElements: QueryList<ElementRef> | undefined;

  /** Whether the frequency chart is loading. */
  @Input()
  get loadingFrequencyChart(): boolean {
    return this._loadingFrequencyChart;
  }
  set loadingFrequencyChart(value: boolean) {
    this._loadingFrequencyChart = coerceBooleanProperty(value);
  }
  private _loadingFrequencyChart = false;

  /** The chart data required to render the frequencies visualization. */
  @Input()
  get frequencyChartData(): AmChartsFrequencySetData | null | undefined {
    return this._frequencyChartData;
  }
  set frequencyChartData(data: AmChartsFrequencySetData | null | undefined) {
    this._frequencyChartData = data;
    if (this.frequencyChartData) {
      this._cleanupChart(this._frequencyChart);
      this._checkAndCreateFrequencyChart();
    } else {
      this._cleanupChart(this._frequencyChart);
    }
  }
  private _frequencyChartData: AmChartsFrequencySetData | null | undefined;

  /** The variable to display statistics for. */
  @Input()
  get variable(): V | null | undefined {
    return this._variable;
  }
  set variable(v: V | null | undefined) {
    this._variable = v;
    this._checkAndCreatePopulatedAndMissingChart();
    this._determineVariableDistributionVisibility();
  }
  private _variable: V | null | undefined;

  /** Chart data for the populated and missing chart. */
  _populatedAndMissingChartData: AmChartsFrequencySetData | undefined;
  /** Whether the variable distribution component should be shown. */
  _showDistribution = false;

  /** The reference to the amchart displaying the frequency data. */
  private _frequencyChart: XYChart | undefined;
  /** The reference to the amchart displaying the populated and missing data. */
  private _populatingAndMissingChart: XYChart | undefined;

  constructor(private zone: NgZone) {
    super();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._cleanupChart(this._frequencyChart);
    this._cleanupChart(this._populatingAndMissingChart);
  }

  private _checkAndCreateFrequencyChart(attempts = 0) {
    // Set timeout ensures previous charts are disposed first
    setTimeout(() => {
      if (!!this.frequencyChartElements && this.frequencyChartElements.length) {
        this._generateFrequencyChart();
      } else if (this.frequencyChartElements) {
        this.frequencyChartElements.changes
          .pipe(
            skipWhile((list) => list.length !== 1),
            take(1)
          )
          .subscribe(() => {
            this._generateFrequencyChart();
          });
      } else if (attempts < 4) {
        setTimeout(() => {
          this._checkAndCreateFrequencyChart(attempts + 1);
        }, 1);
      } else {
        console.error('[RdsVariableStatsComponent] Failed to build frequency chart');
      }
    }, 1);
  }

  private _checkAndCreatePopulatedAndMissingChart(attempts = 0) {
    // Set timeout ensures previous charts are disposed first
    setTimeout(() => {
      if (!!this.populatedAndMissingChartElements && this.populatedAndMissingChartElements.length) {
        this._generatePopulatedAndMissingChart();
      } else if (this.populatedAndMissingChartElements) {
        this.populatedAndMissingChartElements.changes
          .pipe(
            skipWhile((list) => list.length !== 1),
            take(1)
          )
          .subscribe(() => {
            this._generatePopulatedAndMissingChart();
          });
      } else if (attempts < 4) {
        setTimeout(() => {
          this._checkAndCreatePopulatedAndMissingChart(attempts + 1);
        }, 1);
      } else {
        console.error('[RdsVariableStatsComponent] Failed to build populated and missing chart');
      }
    }, 1);
  }

  /** Dispose the AmChart frequency visuzalization. */
  private _cleanupChart(chart: XYChart | undefined) {
    this.zone.runOutsideAngular(() => {
      if (chart) {
        chart.dispose();
      }
    });
  }

  /**
   * Determine whether to show the variable distribution component.
   *
   * There are two requirements:
   * - Cannot be a classified variable
   * - The min cannot equal the max (it's a meaningless visualization in this case)
   */
  private _determineVariableDistributionVisibility() {
    const hasSummaryStatistics = !!this.variable?.summaryStatistics?.sets?.length;
    const hasClassification = !!this.variable?.classificationUri;
    const hasDifferentMinAndMax =
      !!this.variable?.summaryStatistics?.sets?.length &&
      this.variable.summaryStatistics.sets[0].min !== this.variable.summaryStatistics.sets[0].max;
    this._showDistribution = hasSummaryStatistics && !hasClassification && hasDifferentMinAndMax;
  }

  private _generateFrequencyChart() {
    this.zone.runOutsideAngular(async () => {
      this._frequencyChart = await createVerticalBarChart(this.frequencyChartData, this.frequencyChartElements?.first.nativeElement);
    });
  }

  /** Generate the chart config to show the summary statistic's `populated` and `missing` values. */
  private _generatePopulatedAndMissingChart() {
    if (
      this.variable?.summaryStatistics?.sets?.length &&
      isPresent(this.variable.summaryStatistics.sets[0].missing) &&
      isPresent(this.variable.summaryStatistics.sets[0].populated)
    ) {
      // Clean up old chart if it exists
      if (this._populatedAndMissingChartData && this._populatingAndMissingChart) {
        this._cleanupChart(this._populatingAndMissingChart);
      }

      this._populatedAndMissingChartData = {
        dataProvider: [
          {
            value: $localize`Missing`,
            frequency: this.variable.summaryStatistics.sets[0].missing || 0,
          },
          {
            value: $localize`Populated`,
            frequency: this.variable.summaryStatistics.sets[0].populated || 0,
          },
        ],
      };
      this.zone.runOutsideAngular(async () => {
        this._populatingAndMissingChart = await createVerticalBarChart(
          this._populatedAndMissingChartData,
          this.populatedAndMissingChartElements?.first.nativeElement
        );
      });
    } else {
      this._cleanupChart(this._populatingAndMissingChart);
      this._populatedAndMissingChartData = undefined;
    }
  }
}
